import { useState } from "react";
import { HorseArt } from "@/components/game/HorseArt";
import { TimingBarGame } from "@/components/game/minigames/TimingBarGame";
import { ObstaclePathGame } from "@/components/game/minigames/ObstaclePathGame";
import { RhythmGame } from "@/components/game/minigames/RhythmGame";
import { MinigameGate } from "@/components/game/MinigameGate";
import { Trophy, Coins, Award, Star, X } from "lucide-react";

const GAME_FOR = { handling: TimingBarGame, trail: ObstaclePathGame, freestyle: RhythmGame };
const INSTRUCT = {
  handling: "Tap LOCK when the marker sits in the green sweet spot.",
  trail: "Tap GO as the bar fills into the green window for each obstacle.",
  freestyle: "Tap on every beat when the circle pulses. Keep your combo alive!",
};
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export const StableCompete = ({ competition, horse, result, busy, onSubmit, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [perfs, setPerfs] = useState({});
  const [scoring, setScoring] = useState(false);
  const order = competition.order;

  const Shell = ({ children }) => (
    <div className="fixed inset-0 z-[85] overflow-y-auto bg-background" data-testid="stable-compete">
      <div className="mx-auto max-w-lg px-4 py-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-display text-lg font-bold text-primary">🏡 Stable Competition</p>
          {result && <button data-testid="stable-compete-close-x" onClick={onClose} className="rounded-full border border-border p-1 text-muted-foreground"><X size={18} /></button>}
        </div>
        {children}
      </div>
    </div>
  );

  if (result) {
    const r = result;
    return (
      <Shell>
        <div className="rounded-2xl border border-secondary/50 bg-secondary/10 p-5 text-center" data-testid="stable-compete-results">
          <Award size={30} className="mx-auto text-secondary" />
          <p className="mt-1 font-display text-3xl font-extrabold text-primary" data-testid="stable-result-placing">{ordinal(r.placing)} place</p>
          <p className="text-sm text-muted-foreground">of {r.total_entries} · Overall <span className="font-bold text-primary">{r.overall}</span></p>
          <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700"><Coins size={14} /> +{r.prize} coins</p>
          {r.win && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700"><Trophy size={14} /> Win! Raises {horse?.name}'s value</p>}
        </div>
        <div className="mt-3 space-y-2">
          {["handling", "trail", "freestyle"].map((cid) => {
            const c = r.classes[cid];
            return (
              <div key={cid} className="rounded-2xl border border-border bg-card p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-primary">{c.label}</p>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-sm font-bold text-primary">{c.total}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.judge_scores.map((s, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold"><Star size={10} className="text-amber-500" /> {s}</span>)}
                </div>
                <p className="mt-1 text-[11px] italic text-muted-foreground">“{c.comment}”</p>
              </div>
            );
          })}
        </div>
        <button data-testid="stable-compete-done-btn" onClick={onClose}
          className="mt-4 h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Done</button>
      </Shell>
    );
  }

  if (scoring) {
    return <Shell><p className="pt-16 text-center text-muted-foreground" data-testid="stable-compete-scoring">The judges are tallying their scores…</p></Shell>;
  }

  const cid = order[idx];
  const Game = GAME_FOR[cid];
  const conf = competition.classes[cid];
  const onDone = (perf) => {
    const next = { ...perfs, [cid]: perf };
    setPerfs(next);
    if (idx + 1 >= order.length) { setScoring(true); onSubmit(next); }
    else setIdx(idx + 1);
  };
  return (
    <Shell>
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${horse?.color_hex}1a` }}><HorseArt src={horse?.art_image} color={horse?.color_hex} size={44} /></div>
        <div>
          <p className="font-display text-lg font-bold text-primary">{conf.label}</p>
          <p className="text-xs font-semibold text-secondary" data-testid="stable-class-progress">Class {idx + 1} of {order.length} · {horse?.name}</p>
        </div>
        <button data-testid="stable-compete-cancel" onClick={onClose} className="ml-auto rounded-full border border-border p-1 text-muted-foreground"><X size={18} /></button>
      </div>
      <p className="mt-2 rounded-xl bg-muted/60 p-3 text-xs text-foreground/80">{INSTRUCT[cid]}</p>
      <div className="mt-3"><MinigameGate key={cid} gameKey={cid}>{() => <Game difficulty={conf.difficulty} horse={horse} onDone={onDone} />}</MinigameGate></div>
      <div className="mt-4 flex justify-center gap-2">
        {order.map((c, i) => <span key={c} className={`h-2 w-8 rounded-full ${i < idx ? "bg-secondary" : i === idx ? "bg-primary" : "bg-muted"}`} />)}
      </div>
    </Shell>
  );
};
